/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The labeled product region supports keyboard scrolling. */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArrowLeft,
  LuPlus,
  LuSearch,
  LuImageOff,
  LuChevronRight,
} from "react-icons/lu";
import {
  AdminInput,
  AdminTextarea,
  slugify,
} from "../../../../../components/adminForm";
import "../../products/edition/productEditorPage.css";
import { EditorDialog } from "../../products/edition/editorDialog";
import {
  getCategories,
  createCategory,
  updateCategory,
  addProductsToCategory,
  removeProductsFromCategory,
  toggleCategoryActiveStatus,
  toggleProductsStatusInCategory,
} from "../../../../../api/categories";
import { getCatalogs } from "../../../../../api/catalogs";
import { getProductsWithOrganization } from "../../../../../api/products";
import {
  updateFloatPanel,
  requestTableRefresh,
} from "../../../../../reducers/userConfigSlice";
import {
  productMoney,
  productPriceWithTax,
} from "../../products/productListModel";
import {
  normalizeSearch,
  activeValue,
} from "../../../../../components/collectionTools";
const ids = (row) => (row?.Products || []).map((p) => p.productId);
const sameIds = (a, b) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
const productState = (p) =>
  p.ProductCategories?.isActive ?? p.ProductCategory?.isActive;
const states = (row) =>
  Object.fromEntries(
    (row?.Products || []).map((p) => [
      p.productId,
      productState(p) == null ? null : activeValue(productState(p)),
    ]),
  );
const seoOf = (row) => {
  try {
    const seo = typeof row?.seo === "string" ? JSON.parse(row.seo) : row?.seo;
    return seo && typeof seo === "object" ? seo : {};
  } catch {
    return {};
  }
};
const draft = (row) => ({
  name: row?.name || "",
  description: row?.description || "",
  isActive: row ? activeValue(row.isActive) : true,
  seo: {
    ...seoOf(row),
    title: seoOf(row).title || "",
    description: seoOf(row).description || "",
    keywords: seoOf(row).keywords || "",
  },
});
export const CategoryEditor = () => {
  const panel = useSelector(
      (state) => state.aplicationConfig.userConfig.floatPanel,
    ),
    dispatch = useDispatch();
  const [id, setId] = useState(panel.isEmpty ? null : panel.queryId),
    [row, setRow] = useState(null),
    [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null);
  const [members, setMembers] = useState([]),
    [original, setOriginal] = useState([]),
    [availability, setAvailability] = useState({}),
    [originalAvailability, setOriginalAvailability] = useState({});
  const [available, setAvailable] = useState(null),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const [search, setSearch] = useState(""),
    [selecting, setSelecting] = useState(false),
    [selected, setSelected] = useState([]),
    [page, setPage] = useState(1),
    [size, setSize] = useState(10);
  const [picker, setPicker] = useState(false),
    [pickerSearch, setPickerSearch] = useState(""),
    [picks, setPicks] = useState([]),
    [discard, setDiscard] = useState(false);
  const [file, setFile] = useState(null),
    [preview, setPreview] = useState("");
  const editor = useRef(null),
    lock = useRef(false);
  const dirty =
    !!form &&
    (JSON.stringify(form) !== JSON.stringify(baseline) ||
      !sameIds(members, original) ||
      JSON.stringify(availability) !== JSON.stringify(originalAvailability) ||
      !!file);
  const close = () => {
    if (lock.current) return;
    if (dirty) {
      setDiscard(true);
      return;
    }
    dispatch(
      updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
    );
  };
  useEffect(() => {
    let current = true;
    Promise.all([getCategories(), getCatalogs(), getProductsWithOrganization()])
      .then((responses) => {
        if (!current) return;
        if (
          responses[0]?.error &&
          responses[0].message === "No categories found"
        )
          responses[0] = { error: false, data: [] };
        if (responses.some((r) => r?.error !== false || !Array.isArray(r.data)))
          throw Error();
        const loaded = panel.isEmpty
          ? null
          : responses[0].data.find((c) => c.categoryId === panel.queryId);
        if (!panel.isEmpty && (!loaded || !Array.isArray(loaded.Products)))
          throw Error();
        setRow(loaded);
        setForm(draft(loaded));
        setBaseline(draft(loaded));
        setMembers(ids(loaded));
        setOriginal(ids(loaded));
        setAvailability(states(loaded));
        setOriginalAvailability(states(loaded));
        setError("");
        setAvailable({
          catalogs: responses[1].data,
          products: responses[2].data,
        });
      })
      .catch(() => {
        if (current)
          setError("No se ha podido cargar la categoría. Vuelve a intentarlo.");
      });
    return () => {
      current = false;
    };
  }, [panel.queryId, panel.isEmpty, attempt]);
  useEffect(() => {
    const warn = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const changeSeo = (key, value) => {
    setForm((f) => ({ ...f, seo: { ...f.seo, [key]: value } }));
    setSaved(false);
  };
  const read = async (target) => {
    const response = await getCategories();
    const result =
      response?.error === false &&
      Array.isArray(response.data) &&
      response.data.find((c) => c.categoryId === target);
    if (!result || !Array.isArray(result.Products))
      throw Error(
        "No se ha podido comprobar la categoría. Tus cambios se conservan.",
      );
    return result;
  };
  const save = async (event) => {
    event.preventDefault();
    if (lock.current || uncertain || !form) return;
    if (!form.name.trim()) {
      setError("Escribe el nombre de la categoría.");
      editor.current.querySelector("#category-name")?.focus();
      return;
    }
    lock.current = true;
    setBusy(true);
    setSaved(false);
    setError("");
    let target = id;
    const desired = {
      ...form,
      name: form.name.trim(),
      seo: {
        ...form.seo,
        title: form.seo.title || form.name.trim(),
        description:
          form.seo.description || form.description || form.name.trim(),
        keywords: form.seo.keywords || slugify(form.name),
      },
    };
    try {
      const payload = {
        name: desired.name,
        description: desired.description,
        seo: desired.seo,
        ...(file ? { files: [file] } : {}),
      };
      if (!target) {
        setUncertain(true);
        const response = await createCategory({
          ...payload,
          productIds: members,
        });
        target =
          response?.data?.categoryId || response?.data?.dataValues?.categoryId;
        if (response?.error !== false || !target)
          throw Error(
            "No se ha podido confirmar la creación. Vuelve al listado para comprobar si la categoría se ha creado.",
          );
        setId(target);
        setUncertain(false);
        setFile(null);
      } else {
        if (file) setUncertain(true);
        const response = await updateCategory(target, payload);
        if (response?.error !== false) {
          if (file) setUncertain(true);
          throw Error(
            "No se han podido confirmar los detalles. Tus cambios se conservan.",
          );
        }
        setUncertain(false);
        setFile(null);
      }
      // Read before each non-idempotent toggle: a retry must converge, never invert twice.
      let current = await read(target);
      const additions = members.filter(
        (productId) => !ids(current).includes(productId),
      );
      if (additions.length) {
        const response = await addProductsToCategory(target, additions);
        if (response?.error !== false)
          throw Error(
            "No se han podido confirmar los productos añadidos. Tus cambios se conservan.",
          );
      }
      const removals = ids(current).filter(
        (productId) => !members.includes(productId),
      );
      if (removals.length) {
        const response = await removeProductsFromCategory(target, removals);
        if (response?.error !== false)
          throw Error(
            "No se han podido confirmar los productos retirados. Tus cambios se conservan.",
          );
      }
      current = await read(target);
      if (!sameIds(ids(current), members))
        throw Error(
          "No se han podido confirmar los productos de la categoría. Tus cambios se conservan.",
        );
      if (activeValue(current.isActive) !== desired.isActive) {
        await toggleCategoryActiveStatus(target);
        current = await read(target);
        if (activeValue(current.isActive) !== desired.isActive)
          throw Error(
            "No se ha podido confirmar el estado de la categoría. Tus cambios se conservan.",
          );
      }
      for (const productId of members) {
        const wanted = availability[productId];
        if (wanted == null) continue;
        const actual = states(current)[productId];
        if (actual == null)
          throw Error(
            "No se ha podido comprobar el estado de un producto. Tus cambios se conservan.",
          );
        if (actual !== wanted) {
          await toggleProductsStatusInCategory(target, productId);
          current = await read(target);
          if (states(current)[productId] !== wanted)
            throw Error(
              "No se ha podido confirmar el estado de un producto. Tus cambios se conservan.",
            );
        }
      }
      if (JSON.stringify(draft(current)) !== JSON.stringify(desired))
        throw Error(
          "No se ha podido confirmar el guardado. Tus cambios se conservan.",
        );
      setRow(current);
      setForm(draft(current));
      setBaseline(draft(current));
      setOriginal(ids(current));
      setAvailability(states(current));
      setOriginalAvailability(states(current));
      setSaved(true);
      dispatch(requestTableRefresh());
    } catch (e) {
      setError(
        !target
          ? "No se ha podido confirmar la creación. Vuelve al listado para comprobar si la categoría se ha creado."
          : e.message ||
              "No se ha podido guardar la categoría. Tus cambios se conservan.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const products = available?.products || [],
    needle = normalizeSearch(search.trim());
  const memberProducts = members.map(
    (productId) =>
      products.find((p) => p.productId === productId) || {
        productId,
        name: "Producto no disponible",
      },
  );
  const filtered = memberProducts.filter((p) =>
    normalizeSearch(`${p.name || ""} ${p.reference || ""}`).includes(needle),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / size)),
    currentPage = Math.min(page, totalPages),
    visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const catalogs =
    available?.catalogs.filter((c) =>
      (c.Products || []).some((p) => members.includes(p.productId)),
    ) || [];
  const pickerProducts = products.filter(
    (p) =>
      !members.includes(p.productId) &&
      normalizeSearch(`${p.name || ""} ${p.reference || ""}`).includes(
        normalizeSearch(pickerSearch.trim()),
      ),
  );
  return (
    <div
      className="catalogReferenceEditor categoryReferenceEditor"
      ref={editor}
      aria-busy={busy}
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && !picker && !discard) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      }}
    >
      <form onSubmit={save}>
        <header className="catalogEditorHeading">
          <button
            className="catalogBack"
            type="button"
            disabled={busy}
            onClick={close}
          >
            <LuArrowLeft /> Categorías
          </button>
          <div>
            <h2 id="admin-editor-title">
              {row?.name || (panel.isEmpty ? "Nueva categoría" : "Categoría")}
            </h2>
            <div className="catalogHeadingActions">
              <span>Estado</span>
              <button
                type="button"
                role="switch"
                aria-label="Estado de la categoría"
                aria-checked={form?.isActive || false}
                className="catalogSwitch"
                disabled={!form || busy || uncertain}
                onClick={() => change("isActive", !form.isActive)}
              >
                <span />
                {form?.isActive ? "Activa" : "Inactiva"}
              </button>
              <button
                className="catalogPrimary"
                disabled={!form || busy || uncertain || (!dirty && !!id)}
              >
                {busy ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
          {dirty && <p className="catalogFeedback">Cambios sin guardar</p>}
          {saved && <p role="status">Cambios guardados</p>}
        </header>
        {error && (
          <div role="alert" className="catalogError">
            {error}
            {!available && (
              <button type="button" onClick={() => setAttempt((a) => a + 1)}>
                Reintentar
              </button>
            )}
          </div>
        )}
        {uncertain && (
          <p className="catalogError">
            Para comprobar el resultado, vuelve al listado y abre de nuevo la
            categoría.
          </p>
        )}
        {!available ? (
          !error && <p role="status">Cargando categoría…</p>
        ) : (
          <>
            <section className="catalogEditorCard">
              <header>
                <h3>Detalles</h3>
                <p>
                  Cómo se identifica la categoría en el panel y en la tienda.
                </p>
              </header>
              <fieldset
                className="catalogDetailsFields"
                disabled={busy || uncertain}
              >
                <AdminInput
                  id="category-name"
                  label="Nombre"
                  required
                  description="Nombre completo"
                  value={form.name}
                  onChange={(value) => change("name", value)}
                />
                <div className="adminField">
                  <label htmlFor="category-linked-catalog">Catálogo</label>
                  <select id="category-linked-catalog" disabled value="links">
                    <option value="links">
                      {catalogs.length > 1
                        ? `${catalogs.length} catálogos asociados`
                        : catalogs[0]?.name || "Sin catálogo asociado"}
                    </option>
                  </select>
                  <p className="fieldHelp">
                    Catálogos en los que aparecen los productos de la categoría.
                    {catalogs.length > 1 && (
                      <span className="categoryCatalogLinks">
                        {catalogs.map((c) => c.name).join(" · ")}
                      </span>
                    )}
                  </p>
                </div>
                <AdminTextarea
                  id="category-description"
                  label="Descripción"
                  rows={5}
                  placeholder="Describe los productos de esta categoría"
                  description="Se muestra en la tienda junto a la categoría."
                  value={form.description}
                  onChange={(e) => change("description", e.target.value)}
                />
              </fieldset>
              <details className="categoryImage">
                <summary>Imagen de la categoría</summary>
                {(preview || row?.images?.[0]) && (
                  <img
                    src={preview || row.images[0]}
                    alt="Imagen de la categoría"
                  />
                )}
                <label htmlFor="category-image">Cambiar imagen</label>
                <input
                  id="category-image"
                  type="file"
                  accept="image/*"
                  disabled={busy || uncertain}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setSaved(false);
                  }}
                />
              </details>
            </section>
            <section className="catalogEditorCard">
              <header className="categoryProductsHeading">
                <div>
                  <h3>Productos</h3>
                  <p>
                    Selecciona, activa o desactiva los productos de esta
                    categoría.
                  </p>
                </div>
                <div className="catalogProductTools">
                  <label className="catalogSearch">
                    <LuSearch />
                    <input
                      type="search"
                      aria-label="Buscar productos de la categoría"
                      placeholder="Buscar por nombre o referencia"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy || uncertain}
                    onClick={() => {
                      setPicker(true);
                      setPickerSearch("");
                      setPicks([]);
                    }}
                  >
                    <LuPlus /> Agregar productos
                  </button>
                  <button
                    type="button"
                    disabled={busy || uncertain}
                    onClick={() => {
                      setSelecting(!selecting);
                      setSelected([]);
                    }}
                  >
                    {selecting ? "Cancelar selección" : "Seleccionar"}
                  </button>
                </div>
              </header>
              {selecting && (
                <div className="catalogSelection">
                  <span>{selected.length} seleccionados</span>
                  <button
                    type="button"
                    disabled={busy || uncertain || !selected.length}
                    onClick={() => {
                      setMembers((list) =>
                        list.filter((p) => !selected.includes(p)),
                      );
                      setAvailability((previous) =>
                        Object.fromEntries(
                          Object.entries(previous).filter(
                            ([key]) => !selected.includes(key),
                          ),
                        ),
                      );
                      setSelected([]);
                      setSaved(false);
                    }}
                  >
                    Quitar de la categoría
                  </button>
                </div>
              )}
              <div
                className="categoryProductsRegion"
                role="region"
                aria-label="Productos de la categoría"
                tabIndex={0}
              >
                <table className="categoryProductsTable">
                  <thead>
                    <tr>
                      {selecting && <th>Selección</th>}
                      <th>Referencia</th>
                      <th>Imagen</th>
                      <th>Nombre</th>
                      <th>Precio</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((p) => (
                      <tr key={p.productId}>
                        {selecting && (
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${p.name}`}
                              checked={selected.includes(p.productId)}
                              disabled={busy || uncertain}
                              onChange={() =>
                                setSelected((list) =>
                                  list.includes(p.productId)
                                    ? list.filter((x) => x !== p.productId)
                                    : [...list, p.productId],
                                )
                              }
                            />
                          </td>
                        )}
                        <td>{p.reference || "—"}</td>
                        <td>
                          <div className="catalogProductImage">
                            {p.images?.[0] ? (
                              <img src={p.images[0]} alt="" />
                            ) : (
                              <LuImageOff />
                            )}
                          </div>
                        </td>
                        <td>
                          <strong>{p.name}</strong>
                        </td>
                        <td>{productMoney(productPriceWithTax(p))}</td>
                        <td>
                          <button
                            type="button"
                            role="switch"
                            className="catalogSwitch"
                            aria-label={`Disponibilidad de ${p.name} en esta categoría`}
                            aria-checked={availability[p.productId] === true}
                            disabled={
                              busy ||
                              uncertain ||
                              availability[p.productId] == null
                            }
                            onClick={() => {
                              setAvailability((a) => ({
                                ...a,
                                [p.productId]: !a[p.productId],
                              }));
                              setSaved(false);
                            }}
                          >
                            <span />
                            {availability[p.productId] == null
                              ? "No disponible"
                              : availability[p.productId]
                                ? "Activado"
                                : "Desactivado"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!filtered.length && (
                <p className="catalogEmpty">
                  {members.length
                    ? "No hay productos que coincidan con la búsqueda."
                    : "Esta categoría todavía no tiene productos."}
                </p>
              )}
              <footer className="categoryPagination">
                <label>
                  Mostrar{" "}
                  <select
                    aria-label="Productos por página"
                    value={size}
                    onChange={(e) => {
                      setSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 25, 50].map((n) => (
                      <option key={n}>{n}</option>
                    ))}
                  </select>{" "}
                  por página
                </label>
                <span>
                  {filtered.length ? (currentPage - 1) * size + 1 : 0}–
                  {Math.min(currentPage * size, filtered.length)} de{" "}
                  {filtered.length}
                </span>
                <div>
                  <button
                    type="button"
                    aria-label="Página anterior"
                    disabled={currentPage === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ‹
                  </button>
                  <span>{currentPage}</span>
                  <button
                    type="button"
                    aria-label="Página siguiente"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    ›
                  </button>
                </div>
              </footer>
            </section>
            <section className="catalogEditorCard">
              <details className="categorySeo">
                <summary>
                  <div>
                    <h3>SEO</h3>
                    <p>Cómo aparece esta categoría en los buscadores.</p>
                  </div>
                  <span>
                    <LuChevronRight />{" "}
                    <span className="categorySeoExpand">Expandir</span>
                    <span className="categorySeoCollapse">Contraer</span>
                  </span>
                </summary>
                <fieldset
                  className="categorySeoFields"
                  disabled={busy || uncertain}
                >
                  <AdminInput
                    id="category-seo-title"
                    label="Título de la página"
                    value={form.seo.title}
                    placeholder={form.name}
                    description="Recomendado: hasta 60 caracteres"
                    onChange={(v) => changeSeo("title", v)}
                  />
                  <AdminInput
                    id="category-seo-slug"
                    label="Identificador de URL"
                    value={form.seo.keywords}
                    placeholder={slugify(form.name)}
                    onChange={(v) => changeSeo("keywords", v)}
                  />
                  <AdminTextarea
                    id="category-seo-description"
                    label="Meta descripción"
                    value={form.seo.description}
                    rows={3}
                    description="Recomendado: hasta 155 caracteres"
                    onChange={(e) => changeSeo("description", e.target.value)}
                  />
                </fieldset>
              </details>
              <div className="categorySeoPreview">
                <strong>{form.seo.title || form.name}</strong>
                <span>/{form.seo.keywords || slugify(form.name)}</span>
              </div>
            </section>
          </>
        )}
      </form>
      {picker && (
        <EditorDialog
          title="Agregar productos"
          description="Selecciona los productos que quieres añadir a la categoría."
          onClose={() => setPicker(false)}
          footer={
            <>
              <button type="button" onClick={() => setPicker(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="catalogPrimary"
                disabled={!picks.length}
                onClick={() => {
                  setMembers((list) => [...new Set([...list, ...picks])]);
                  setAvailability((a) => ({
                    ...a,
                    ...Object.fromEntries(picks.map((key) => [key, true])),
                  }));
                  setSaved(false);
                  setPicker(false);
                }}
              >
                Añadir
              </button>
            </>
          }
        >
          <label className="catalogSearch">
            <LuSearch />
            <input
              type="search"
              aria-label="Buscar productos para añadir"
              placeholder="Buscar por nombre o referencia"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
            />
          </label>
          <div className="referencePickerOptions">
            {pickerProducts.map((p) => (
              <label key={p.productId}>
                <input
                  type="checkbox"
                  aria-label={`Añadir ${p.name}`}
                  checked={picks.includes(p.productId)}
                  onChange={() =>
                    setPicks((list) =>
                      list.includes(p.productId)
                        ? list.filter((x) => x !== p.productId)
                        : [...list, p.productId],
                    )
                  }
                />
                <span>
                  <strong>{p.name}</strong>
                  <small>
                    {p.reference || "Sin referencia"} ·{" "}
                    {productMoney(productPriceWithTax(p))}
                  </small>
                </span>
              </label>
            ))}
            {!pickerProducts.length && (
              <p>No hay productos disponibles que coincidan.</p>
            )}
          </div>
        </EditorDialog>
      )}
      {discard && (
        <EditorDialog
          title="Descartar cambios"
          description="Hay cambios sin guardar en esta categoría."
          onClose={() => setDiscard(false)}
          footer={
            <>
              <button type="button" onClick={() => setDiscard(false)}>
                Seguir editando
              </button>
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    updateFloatPanel({
                      visible: false,
                      queryId: null,
                      isEmpty: true,
                    }),
                  )
                }
              >
                Descartar y volver
              </button>
            </>
          }
        />
      )}
    </div>
  );
};
